import { defineStore } from 'pinia'
import type { CartLineItem } from '~/types'
import { normalizeCartItems } from '~/utils/cartNormalize'
import { resolveCurrentClientShopSlug } from '~/utils/resolveShopSlug'
import { cartLineCount, linePriceUnit, normalizeServiceQuantity } from '~/utils/shopServices'
import { readShopStorage, writeShopStorage } from '~/utils/shopStorage'

function currentCartShopSlug(): string {
  return resolveCurrentClientShopSlug()
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartLineItem[]>([])
  const isDrawerOpen = ref(false)
  const compareProducts = ref<number[]>([])
  const bumpKey = ref(0)

  const itemCount = computed(() => cartLineCount(items.value))

  const subtotal = computed(() =>
    items.value.reduce((sum, item) => sum + item.totalPrice, 0),
  )

  function hydrateFromStorage() {
    if (!import.meta.client) return
    const slug = currentCartShopSlug()
    try {
      const stored = readShopStorage(slug, 'cart')
      if (stored) {
        const parsed = JSON.parse(stored) as CartLineItem[]
        items.value = Array.isArray(parsed) ? normalizeCartItems(parsed) : []
      }
    } catch {
      items.value = []
    }

    try {
      const storedCompare = readShopStorage(slug, 'compare')
      if (storedCompare) {
        const parsed = JSON.parse(storedCompare)
        if (Array.isArray(parsed)) {
          compareProducts.value = parsed
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
            .slice(0, 4)
        }
      }
    } catch {
      compareProducts.value = []
    }
  }

  function saveToStorage() {
    if (import.meta.client) {
      writeShopStorage(currentCartShopSlug(), 'cart', JSON.stringify(items.value))
    }
  }

  function saveCompareToStorage() {
    if (import.meta.client) {
      writeShopStorage(currentCartShopSlug(), 'compare', JSON.stringify(compareProducts.value))
    }
  }

  function setItems(newItems: CartLineItem[]) {
    items.value = newItems
    saveToStorage()
  }

  function sameLine(
    a: { productId: number; variantId?: number; customFieldsKey?: string },
    b: { productId: number; variantId?: number; customFieldsKey?: string },
  ) {
    return (
      a.productId === b.productId
      && a.variantId === b.variantId
      && (a.customFieldsKey || '') === (b.customFieldsKey || '')
    )
  }

  function addItem(item: CartLineItem) {
    const existing = items.value.find((i) => sameLine(i, item))
    if (existing) {
      const mergedQty = existing.quantity + item.quantity
      existing.quantity = linePriceUnit(item) || linePriceUnit(existing)
        ? (normalizeServiceQuantity(mergedQty) ?? mergedQty)
        : mergedQty
      existing.unitPrice = item.unitPrice
      existing.totalPrice = existing.unitPrice * existing.quantity
      existing.customFields = item.customFields
      existing.customFieldsKey = item.customFieldsKey
      existing.customFieldsSnapshot = item.customFieldsSnapshot
      existing.packageItemProductIds = item.packageItemProductIds
      existing.variationSelections = item.variationSelections
      existing.priceUnit = item.priceUnit ?? existing.priceUnit
    } else {
      items.value.push(item)
    }
    bumpKey.value++
    saveToStorage()
  }

  function updateQuantity(
    productId: number,
    quantity: number,
    variantId?: number,
    customFieldsKey?: string,
  ) {
    const item = items.value.find((i) =>
      sameLine(i, { productId, variantId, customFieldsKey }),
    )
    if (item) {
      item.quantity = quantity
      item.totalPrice = item.unitPrice * quantity
      saveToStorage()
    }
  }

  function removeItem(productId: number, variantId?: number, customFieldsKey?: string) {
    items.value = items.value.filter(
      (i) => !sameLine(i, { productId, variantId, customFieldsKey }),
    )
    saveToStorage()
  }

  function clear() {
    items.value = []
    saveToStorage()
  }

  function pruneReservedItems(reservedIds: number[]): number {
    if (!reservedIds.length || !items.value.length) return 0

    const reserved = new Set(reservedIds)
    const before = items.value.length
    items.value = items.value.filter((item) => !reserved.has(item.productId))
    if (items.value.length !== before) {
      saveToStorage()
    }
    return before - items.value.length
  }

  function setDrawerOpen(open: boolean) {
    isDrawerOpen.value = open
  }

  function toggleCompare(productId: number) {
    const index = compareProducts.value.indexOf(productId)
    if (index >= 0) {
      compareProducts.value.splice(index, 1)
    } else if (compareProducts.value.length < 4) {
      compareProducts.value.push(productId)
    }
    saveCompareToStorage()
  }

  function setCompareProducts(ids: number[]) {
    compareProducts.value = [...new Set(ids.filter((id) => Number.isFinite(id) && id > 0))].slice(0, 4)
    saveCompareToStorage()
  }

  function isInCompare(productId: number) {
    return compareProducts.value.includes(productId)
  }

  function clearCompare() {
    compareProducts.value = []
    saveCompareToStorage()
  }

  return {
    items,
    isDrawerOpen,
    compareProducts,
    bumpKey,
    itemCount,
    subtotal,
    hydrateFromStorage,
    setItems,
    addItem,
    updateQuantity,
    removeItem,
    clear,
    pruneReservedItems,
    setDrawerOpen,
    toggleCompare,
    setCompareProducts,
    isInCompare,
    clearCompare,
  }
})
